import { useQuery } from "@tanstack/react-query";
import { Link } from "@/lib/router-compat";
import { useLanguage } from "@/hooks/useLanguage";
import type { SiteLang } from "@/hooks/useLanguage";
import {
  Sparkles, BookOpen,
  Bell, TrendingUp, Shield, Calendar, ChevronRight,
} from "lucide-react";
import { motion } from "framer-motion";
import KnowledgeHeroAnimation from "@/components/KnowledgeHeroAnimation";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ScrollToTop from "@/components/ScrollToTop";
import BlogSEO from "@/components/blog/BlogSEO";
import HubSpotInlineForm from "@/components/HubSpotInlineForm";
import { supabase } from "@/integrations/supabase/client";
import { localizeContentRow } from "@/lib/localizeContent";
import { localizeHref } from "@/lib/localizeHref";

/* ── i18n dictionary ── */
const dict: Record<string, Record<SiteLang, string>> = {
  seoTitle: {
    en: "Newsletter | Life Sciences Insights | QbD Group",
    es: "Newsletter | Insights en Life Sciences | QbD Group",
    "zh-cn": "Newsletter | 生命科学洞察 | QbD Group",
  },
  seoDesc: {
    en: "Subscribe to QbD Group's newsletter for expert regulatory insights, compliance guidance, and industry analysis delivered to your inbox.",
    es: "Suscríbete a la newsletter de QbD Group para recibir insights regulatorios, orientación en cumplimiento normativo y análisis de la industria directamente en tu bandeja de entrada.",
    "zh-cn": "订阅QbD Group通讯，获取专家法规洞察、合规指导和行业分析。",
  },
  badge: { en: "NEWSLETTER", es: "NEWSLETTER", "zh-cn": "通讯" },
  heroTitle1: { en: "Stay ahead in", es: "Mantente al día en", "zh-cn": "领先于" },
  heroTitle2: { en: "life sciences", es: "life sciences", "zh-cn": "生命科学" },
  heroDesc: {
    en: "Expert regulatory insights, compliance guidance, and industry analysis, delivered to your inbox every two weeks.",
    es: "Insights regulatorios de expertos, orientación en cumplimiento normativo y análisis de la industria — directamente en tu bandeja de entrada cada dos semanas.",
    "zh-cn": "专家法规洞察、合规指导和行业分析，每两周直达您的收件箱。",
  },
  emailPlaceholder: {
    en: "Enter your work email",
    es: "Introduce tu email profesional",
    "zh-cn": "输入您的工作邮箱",
  },
  subscribe: { en: "Subscribe", es: "Suscribirme", "zh-cn": "订阅" },
  subscribed: { en: "You're subscribed!", es: "¡Te has suscrito!", "zh-cn": "您已订阅！" },
  subscribedDesc: {
    en: "Thanks for joining. We'll keep you posted on the latest insights.",
    es: "Gracias por unirte. Te mantendremos informado con los últimos insights.",
    "zh-cn": "感谢加入。我们会为您推送最新洞察。",
  },
  alreadySubscribed: {
    en: "You're already subscribed!",
    es: "¡Ya estás suscrito!",
    "zh-cn": "您已经订阅了！",
  },
  noSpam: { en: "No spam, ever. Unsubscribe anytime. Read our", es: "Sin spam, nunca. Cancela cuando quieras. Lee nuestra", "zh-cn": "绝无垃圾邮件。随时退订。阅读我们的" },
  privacyPolicy: { en: "Privacy Policy", es: "Política de privacidad", "zh-cn": "隐私政策" },
  whatYouReceive: { en: "What you'll receive", es: "Lo que recibirás", "zh-cn": "您将收到什么" },
  whatYouReceiveDesc: {
    en: "Curated content from our team of 250+ life sciences consultants across Europe.",
    es: "Contenido seleccionado por nuestro equipo de más de 250 consultores de life sciences en toda Europa.",
    "zh-cn": "来自我们遍布欧洲的250+名生命科学顾问团队精选的内容。",
  },
  preview: { en: "PREVIEW", es: "VISTA PREVIA", "zh-cn": "预览" },
  seeInbox: { en: "See what lands in your inbox", es: "Mira lo que recibirás en tu bandeja", "zh-cn": "看看您的收件箱会收到什么" },
  seeInboxDesc: {
    en: "A sneak peek at recent editions of the QbD Group newsletter.",
    es: "Un adelanto de las ediciones recientes de la newsletter de QbD Group.",
    "zh-cn": "QbD Group通讯近期版本预览。",
  },
  biweekly: { en: "Bi-weekly", es: "Quincenal", "zh-cn": "双周刊" },
  featured: { en: "Featured", es: "Destacado", "zh-cn": "推荐" },
  recentEditions: { en: "RECENT EDITIONS", es: "EDICIONES RECIENTES", "zh-cn": "近期版本" },
  tasteInside: { en: "A taste of what's inside", es: "Una muestra de lo que encontrarás", "zh-cn": "内容精选一览" },
  tasteInsideDesc: {
    en: "Here's a selection of recent articles our subscribers received first.",
    es: "Una selección de los artículos recientes que nuestros suscriptores recibieron primero.",
    "zh-cn": "以下是我们订阅者最先收到的近期文章精选。",
  },
  readArticle: { en: "Read article", es: "Leer artículo", "zh-cn": "阅读文章" },
  dontMiss: { en: "Don't miss a beat", es: "No te pierdas nada", "zh-cn": "不错过任何动态" },
  dontMissDesc: {
    en: "Join thousands of life sciences professionals who rely on our insights to stay compliant and competitive.",
    es: "Únete a miles de profesionales de life sciences que confían en nuestros insights para mantenerse al día en cumplimiento y competitividad.",
    "zh-cn": "加入数千名依赖我们洞察保持合规和竞争力的生命科学专业人士。",
  },
  errorGeneric: {
    en: "Something went wrong. Please try again.",
    es: "Ha ocurrido un error. Inténtalo de nuevo.",
    "zh-cn": "出现问题，请重试。",
  },
  emailInvalid: {
    en: "Please enter a valid email address",
    es: "Introduce una dirección de email válida",
    "zh-cn": "请输入有效的电子邮件地址",
  },
  emailTooLong: {
    en: "Email is too long",
    es: "El email es demasiado largo",
    "zh-cn": "邮箱地址过长",
  },
  // Value props
  vpRegIntelTitle: { en: "Regulatory intelligence", es: "Inteligencia regulatoria", "zh-cn": "法规情报" },
  vpRegIntelDesc: {
    en: "Be the first to know about regulatory shifts impacting pharma, biotech, and medical devices.",
    es: "Sé el primero en conocer los cambios regulatorios que impactan en farma, biotecnología y Medical Devices.",
    "zh-cn": "率先了解影响制药、生物技术和医疗器械的法规变化。",
  },
  vpExpertTitle: { en: "Expert analysis", es: "Análisis de expertos", "zh-cn": "专家分析" },
  vpExpertDesc: {
    en: "Deep-dive articles from seasoned consultants with decades of hands-on GxP experience.",
    es: "Artículos en profundidad de consultores experimentados con décadas de experiencia práctica en GxP.",
    "zh-cn": "来自经验丰富顾问的深度文章，拥有数十年GxP实践经验。",
  },
  vpComplianceTitle: { en: "Compliance insights", es: "Insights de cumplimiento", "zh-cn": "合规洞察" },
  vpComplianceDesc: {
    en: "Practical guidance on quality management, validation, and audit-readiness.",
    es: "Orientación práctica sobre gestión de calidad, validación y preparación para auditorías.",
    "zh-cn": "关于质量管理、验证和审计准备的实用指南。",
  },
  vpEventTitle: { en: "Event & webinar alerts", es: "Alertas de eventos y webinars", "zh-cn": "活动与网络研讨会提醒" },
  vpEventDesc: {
    en: "Early access to webinars, workshops, and industry events.",
    es: "Acceso anticipado a webinars, talleres y eventos de la industria.",
    "zh-cn": "提前获取网络研讨会、工作坊和行业活动信息。",
  },
  // Interest chips
  intPharma: { en: "Pharma", es: "Farma", "zh-cn": "制药" },
  intBiotech: { en: "Biotech", es: "Biotecnología", "zh-cn": "生物技术" },
  intDevices: { en: "Medical Devices", es: "Medical Devices", "zh-cn": "医疗器械" },
  intIvd: { en: "IVD", es: "IVD", "zh-cn": "IVD" },
};

const t = (key: string, lang: SiteLang) => dict[key]?.[lang] ?? dict[key]?.en ?? key;

const NEWSLETTER_FORM_ID = "e49b7f08-4495-4cb4-afa2-b485f007f8a2";

/* ── Main component ── */
const Newsletter = () => {
  const lang = useLanguage();

  const VALUE_PROPS = [
    { icon: TrendingUp, title: t("vpRegIntelTitle", lang), description: t("vpRegIntelDesc", lang) },
    { icon: BookOpen, title: t("vpExpertTitle", lang), description: t("vpExpertDesc", lang) },
    { icon: Shield, title: t("vpComplianceTitle", lang), description: t("vpComplianceDesc", lang) },
    { icon: Calendar, title: t("vpEventTitle", lang), description: t("vpEventDesc", lang) },
  ];

  /* Fetch recent articles as "example content" */
  const { data: recentPosts } = useQuery({
    queryKey: ["newsletter-examples", lang],
    queryFn: async () => {
      // For ES, fetch Spanish blog posts; otherwise EN
      const langFilter = lang === "es" ? "es" : "en";
      const { data } = await supabase
        .from("blog_posts")
        .select("title, slug, category, featured_image, published_at, summary")
        .eq("lang", langFilter)
        .not("published_at", "is", null)
        .order("published_at", { ascending: false })
        .limit(6);
      return data ?? [];
    },
  });

  const privacyHref = localizeHref("/en/privacy-policy", lang);

  return (
    <div className="min-h-screen bg-background">
      <ScrollToTop />
      <BlogSEO
        type="listing"
        title={t("seoTitle", lang)}
        description={t("seoDesc", lang)}
        basePath="/en/newsletter"
        breadcrumbLabel="Newsletter"
      />

      <Navbar />

      {/* ── Hero ── */}
      <section className="relative pt-32 pb-20 sm:pt-40 sm:pb-28 bg-primary overflow-hidden">
        <KnowledgeHeroAnimation />

        <div className="relative container mx-auto px-6 lg:px-10 max-w-4xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >

            <h1 className="mt-4 text-3xl sm:text-5xl lg:text-[3.5rem] font-bold text-white leading-[1.15] tracking-tight">
              {t("heroTitle1", lang)}{" "}
              <span className="text-accent">{t("heroTitle2", lang)}</span>
            </h1>

            <p className="mt-6 text-lg sm:text-xl text-white/70 max-w-2xl mx-auto leading-relaxed">
              {t("heroDesc", lang)}
            </p>
          </motion.div>

          {/* Hero signup form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-10 max-w-xl mx-auto"
          >
            <div className="qbd-newsletter-form qbd-newsletter-form--dark text-left">
              <HubSpotInlineForm
                formId={NEWSLETTER_FORM_ID}
                targetId="newsletter-hubspot-form-hero"
              />
            </div>

            <p className="mt-4 text-[11px] text-white/30">
              {t("noSpam", lang)}{" "}
              <a href={privacyHref} className="underline hover:text-white/50 transition-colors">
                {t("privacyPolicy", lang)}
              </a>
              .
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── What you'll get ── */}
      <section className="py-20 sm:py-28 bg-background">
        <div className="container mx-auto px-6 lg:px-10 max-w-5xl">
          <div className="text-center mb-14">
            <span className="text-xs font-bold uppercase tracking-widest text-accent">{t("badge", lang)}</span>
            <h2 className="mt-3 text-2xl sm:text-3xl font-bold text-foreground">
              {t("whatYouReceive", lang)}
            </h2>
            <p className="mt-3 text-muted-foreground max-w-xl mx-auto">
              {t("whatYouReceiveDesc", lang)}
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            {VALUE_PROPS.map((prop, i) => (
              <motion.div
                key={prop.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className="group flex gap-5 p-6 rounded-2xl border border-border/60 bg-card hover:border-accent/30 hover:shadow-md transition-all duration-300"
              >
                <div className="shrink-0 text-accent mt-0.5">
                  <prop.icon size={28} strokeWidth={1.6} />
                </div>
                <div>
                  <h3 className="font-bold text-foreground">{prop.title}</h3>
                  <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">
                    {prop.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Newsletter Mockups ── */}
      <section className="py-20 sm:py-28 bg-background border-t border-border/40">
        <div className="container mx-auto px-6 lg:px-10 max-w-5xl">
          <div className="text-center mb-14">
            <span className="text-xs font-bold uppercase tracking-widest text-accent">
              {t("preview", lang)}
            </span>
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground">
              {t("seeInbox", lang)}
            </h2>
            <p className="mt-3 text-muted-foreground max-w-xl mx-auto">
              {t("seeInboxDesc", lang)}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Mockup 1 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5 }}
              className="rounded-2xl border border-border/60 bg-card shadow-sm overflow-hidden"
            >
              <div className="bg-primary px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center">
                    <Sparkles size={14} className="text-accent" />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-white tracking-wide">QbD INSIGHTS</p>
                    <p className="text-[10px] text-white/50">Edition #47 · Jan 2026</p>
                  </div>
                </div>
                <span className="text-[10px] text-white/40 bg-white/10 px-2 py-0.5 rounded-full">{t("biweekly", lang)}</span>
              </div>
              <div className="p-6 space-y-4">
                <div className="flex gap-3">
                  <div className="w-1 rounded-full bg-accent shrink-0" />
                  <div>
                    <p className="text-xs font-semibold text-accent uppercase tracking-wider">{t("featured", lang)}</p>
                    <p className="text-sm font-bold text-foreground mt-0.5">EU MDR Transition: What Manufacturers Need to Know Before the 2027 Deadline</p>
                    <p className="text-xs text-muted-foreground mt-1">The latest guidance from the European Commission clarifies key requirements for legacy devices…</p>
                  </div>
                </div>
                <div className="border-t border-border/50 pt-4 space-y-3">
                  <div className="flex items-start gap-2">
                    <Shield size={12} className="text-accent mt-0.5 shrink-0" />
                    <p className="text-xs text-muted-foreground"><span className="font-semibold text-foreground">IVDR Update:</span> Notified Body capacity report, current state and projections</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <TrendingUp size={12} className="text-accent mt-0.5 shrink-0" />
                    <p className="text-xs text-muted-foreground"><span className="font-semibold text-foreground">Industry Trend:</span> AI-driven pharmacovigilance, regulatory expectations in 2026</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <Calendar size={12} className="text-accent mt-0.5 shrink-0" />
                    <p className="text-xs text-muted-foreground"><span className="font-semibold text-foreground">Upcoming:</span> Free webinar on Post-Market Surveillance best practices, Feb 12</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Mockup 2 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="rounded-2xl border border-border/60 bg-card shadow-sm overflow-hidden"
            >
              <div className="bg-primary px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center">
                    <Sparkles size={14} className="text-accent" />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-white tracking-wide">QbD INSIGHTS</p>
                    <p className="text-[10px] text-white/50">Edition #46 · Dec 2025</p>
                  </div>
                </div>
                <span className="text-[10px] text-white/40 bg-white/10 px-2 py-0.5 rounded-full">{t("biweekly", lang)}</span>
              </div>
              <div className="p-6 space-y-4">
                <div className="flex gap-3">
                  <div className="w-1 rounded-full bg-accent shrink-0" />
                  <div>
                    <p className="text-xs font-semibold text-accent uppercase tracking-wider">{t("featured", lang)}</p>
                    <p className="text-sm font-bold text-foreground mt-0.5">CDx & Companion Diagnostics: Navigating the Evolving EU Regulatory Landscape</p>
                    <p className="text-xs text-muted-foreground mt-1">As precision medicine advances, companion diagnostic regulation is catching up. Here's your roadmap…</p>
                  </div>
                </div>
                <div className="border-t border-border/50 pt-4 space-y-3">
                  <div className="flex items-start gap-2">
                    <BookOpen size={12} className="text-accent mt-0.5 shrink-0" />
                    <p className="text-xs text-muted-foreground"><span className="font-semibold text-foreground">White Paper:</span> Quality by Design in pharmaceutical development, 2025 edition</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <Shield size={12} className="text-accent mt-0.5 shrink-0" />
                    <p className="text-xs text-muted-foreground"><span className="font-semibold text-foreground">Compliance Alert:</span> EMA updates GMP Annex 1, key changes for sterile manufacturing</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <TrendingUp size={12} className="text-accent mt-0.5 shrink-0" />
                    <p className="text-xs text-muted-foreground"><span className="font-semibold text-foreground">Case Study:</span> How we helped a biotech startup achieve EU market launch in 9 months</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Example content ── */}
      {recentPosts && recentPosts.length > 0 && (
        <section className="py-20 sm:py-28 bg-secondary/40">
          <div className="container mx-auto px-6 lg:px-10 max-w-6xl">
            <div className="text-center mb-14">
              <span className="text-xs font-bold uppercase tracking-widest text-accent">
                {t("recentEditions", lang)}
              </span>
              <h2 className="text-2xl sm:text-3xl font-bold text-foreground">
                {t("tasteInside", lang)}
              </h2>
              <p className="mt-3 text-muted-foreground max-w-xl mx-auto">
                {t("tasteInsideDesc", lang)}
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {recentPosts.map((post, i) => (
                <motion.div
                  key={post.slug}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                >
                  <Link
                    to={localizeHref(`/en/blog/${post.slug}`, lang)}
                    className="group block rounded-2xl overflow-hidden border border-border/60 bg-card hover:border-accent/30 hover:shadow-lg transition-all duration-300"
                  >
                    {post.featured_image && (
                      <div className="aspect-[16/9] overflow-hidden">
                        <img
                          src={post.featured_image}
                          alt={post.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      </div>
                    )}
                    <div className="p-5">
                      {post.category && (
                        <span className="text-[11px] font-semibold text-accent uppercase tracking-wider">
                          {post.category}
                        </span>
                      )}
                      <h3 className="mt-1.5 font-bold text-foreground leading-snug line-clamp-2 group-hover:text-accent transition-colors">
                        {post.title}
                      </h3>
                      {post.summary && (
                        <p className="mt-2 text-sm text-muted-foreground line-clamp-2">
                          {post.summary}
                        </p>
                      )}
                      <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-accent">
                        {t("readArticle", lang)} <ChevronRight size={12} />
                      </span>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── Bottom CTA ── */}
      <section className="py-20 sm:py-28 bg-background">
        <div className="container mx-auto px-6 lg:px-10 max-w-2xl text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-accent/10 text-accent mb-6">
            <Bell size={28} />
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground">
            {t("dontMiss", lang)}
          </h2>
          <p className="mt-3 text-muted-foreground max-w-lg mx-auto">
            {t("dontMissDesc", lang)}
          </p>

          <div className="mt-8 max-w-xl mx-auto">
            <div className="qbd-newsletter-form qbd-newsletter-form--light text-left">
              <HubSpotInlineForm
                formId={NEWSLETTER_FORM_ID}
                targetId="newsletter-hubspot-form-bottom"
              />
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Newsletter;
